import React, { useEffect, useMemo, useState } from "react";
import "../styles/AdminContactMessages.css";

function AdminContactMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  // UI state
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | new | read | archived
  const [selected, setSelected] = useState(null); // mensaje seleccionado (panel detalle)

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch("https://alaiak.eus/api/alaiak/admin/contact-messages", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("No autorizado o error de red");
      const data = await res.json();
      setMessages(data);

      // si teníamos uno seleccionado, refrescarlo con la versión actual
      if (selected) {
        const updated = data.find((m) => m.id === selected.id);
        if (updated) setSelected(updated);
      }
    } catch (err) {
      setError(err.message || "Error cargando mensajes");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return messages
      .filter((m) => (statusFilter === "all" ? true : m.status === statusFilter))
      .filter((m) => {
        if (!q) return true;
        return (
          (m.name || "").toLowerCase().includes(q) ||
          (m.contact || "").toLowerCase().includes(q) ||
          (m.event_type || "").toLowerCase().includes(q) ||
          (m.message || "").toLowerCase().includes(q)
        );
      });
  }, [messages, query, statusFilter]);

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  const patchStatus = async (id, status) => {
    setError("");
    setBusyId(id);
    try {
      const res = await fetch(`https://alaiak.eus/api/alaiak/admin/contact-messages/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Error al actualizar estado");

      // refresco local (sin refetch completo)
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
      setSelected((prev) => (prev && prev.id === id ? { ...prev, status } : prev));
    } catch (err) {
      setError(err.message || "Error actualizando");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    setError("");
    setBusyId(id);
    try {
      const res = await fetch(`https://alaiak.eus/api/alaiak/admin/contact-messages/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Error al borrar mensaje");

      setMessages((prev) => prev.filter((m) => m.id !== id));
      setSelected((prev) => (prev && prev.id === id ? null : prev));
    } catch (err) {
      setError(err.message || "Error borrando");
    } finally {
      setBusyId(null);
    }
  };

  const statusLabel = (s) => {
    if (s === "new") return "Nuevo";
    if (s === "read") return "Leído";
    if (s === "archived") return "Archivado";
    return s || "";
  };

  return (
    <div className="admin-contact-panel">
      <div className="admin-contact-header">
        <h2>Mensajes de Contacto</h2>
        <div className="admin-contact-actions">
          <button className="admin-contact-refresh" onClick={fetchMessages} disabled={loading}>
            {loading ? "Cargando..." : "Actualizar"}
          </button>
        </div>
      </div>

      <div className="admin-contact-toolbar">
        <input
          className="admin-contact-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, contacto o texto..."
        />

        <select
          className="admin-contact-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos</option>
          <option value="new">Nuevos</option>
          <option value="read">Leídos</option>
          <option value="archived">Archivados</option>
        </select>
      </div>

      {error && <div className="admin-contact-error">{error}</div>}

      <div className="admin-contact-grid">
        <div className="admin-contact-list">
          {filtered.length === 0 ? (
            <div className="admin-contact-empty">
              {messages.length === 0 ? "No hay mensajes." : "No hay resultados con esos filtros."}
            </div>
          ) : (
            <table className="admin-contact-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Nombre</th>
                  <th>Contacto</th>
                  <th>Motivo</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr
                    key={m.id}
                    className={`row-status-${m.status} ${selected?.id === m.id ? "row-selected" : ""}`}
                    onClick={() => setSelected(m)}
                    role="button"
                    tabIndex={0}
                  >
                    <td>{formatDate(m.created_at)}</td>
                    <td>{m.name}</td>
                    <td>{m.contact}</td>
                    <td>{m.event_type || "-"}</td>
                    <td>
                      <span className={`badge badge--${m.status}`}>{statusLabel(m.status)}</span>
                    </td>
                    <td className="admin-contact-row-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn-small"
                        onClick={() => patchStatus(m.id, "read")}
                        disabled={busyId === m.id}
                        title="Marcar como leído"
                      >
                        Leer
                      </button>
                      <button
                        className="btn-small"
                        onClick={() => patchStatus(m.id, "archived")}
                        disabled={busyId === m.id}
                        title="Archivar"
                      >
                        Archivar
                      </button>
                      <button
                        className="btn-small btn-danger"
                        onClick={() => handleDelete(m.id)}
                        disabled={busyId === m.id}
                        title="Eliminar"
                      >
                        Borrar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <aside className="admin-contact-detail">
          {!selected ? (
            <div className="admin-contact-detail-empty">
              Selecciona un mensaje para ver el detalle.
            </div>
          ) : (
            <div className="admin-contact-card">
              <div className="admin-contact-card-header">
                <div>
                  <div className="admin-contact-card-title">{selected.name}</div>
                  <div className="admin-contact-card-meta">
                    <span>{formatDate(selected.created_at)}</span>
                    <span className={`badge badge--${selected.status}`}>{statusLabel(selected.status)}</span>
                  </div>
                </div>

                <div className="admin-contact-card-actions">
                  <button
                    className="btn-small"
                    onClick={() => patchStatus(selected.id, "read")}
                    disabled={busyId === selected.id}
                  >
                    Marcar leído
                  </button>
                  <button
                    className="btn-small"
                    onClick={() => patchStatus(selected.id, "archived")}
                    disabled={busyId === selected.id}
                  >
                    Archivar
                  </button>
                </div>
              </div>

              <div className="admin-contact-field">
                <div className="label">Contacto</div>
                <div className="value">{selected.contact}</div>
              </div>

              <div className="admin-contact-field">
                <div className="label">Motivo</div>
                <div className="value">{selected.event_type || "-"}</div>
              </div>

              <div className="admin-contact-field">
                <div className="label">Mensaje</div>
                <div className="value value-message">{selected.message}</div>
              </div>

              <div className="admin-contact-field">
                <div className="label">Acciones rápidas</div>
                <div className="value admin-contact-quick">
                  {/* Si es email, intenta abrir mailto. Si es teléfono, tel. */}
                  {selected.contact?.includes("@") ? (
                    <a className="link" href={`mailto:${selected.contact}`}>
                      Responder por email
                    </a>
                  ) : (
                    <a className="link" href={`tel:${selected.contact}`}>
                      Llamar
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default AdminContactMessages;
